import { computed } from 'vue'

import { locationService } from '@/api/services/location'
import type { ApiRestaurant } from '@/api/types/location'
import { useCookie } from '@/composables/useCookie'
import type { LngLat } from '@/composables/useLocation'
import { useRequest } from '@/composables/useRequest'
import { RESTAURANT_COOKIE } from '@/constants/storage'
import { formatWorkTime } from '@/utils/time'

/** Точка на карте: и филиал, и найденный адрес приводятся к этому виду. */
export interface MapMarker {
  id: string
  title: string
  coordinates: LngLat
  address?: string
  workTime?: string
}

export function toMarker(restaurant: ApiRestaurant): MapMarker {
  return {
    id: restaurant.id,
    title: restaurant.name,
    coordinates: [Number(restaurant.longitude), Number(restaurant.latitude)],
    address: restaurant.address,
    workTime: formatWorkTime(restaurant.startOfWorkingTime, restaurant.endOfWorkingTime),
  }
}

/** Филиалы бренда: список для самовывоза и маркеры карты. */
export function useRestaurants() {
  const restaurantId = useCookie(RESTAURANT_COOKIE)

  const request = useRequest(locationService.restaurants)

  /** Выключенные филиалы бэкенд отдаёт вместе с остальными — отсеиваем. */
  const restaurants = computed(() => (request.data.value ?? []).filter((rest) => rest.isActive))
  const loading = request.isLoading

  const activeRestaurant = computed(() =>
    restaurants.value.find((rest) => rest.id === restaurantId.value),
  )

  const markers = computed<MapMarker[]>(() => restaurants.value.map(toMarker))

  function setRestaurantId(value: string): void {
    restaurantId.value = value
  }

  async function getRestaurants(): Promise<void> {
    await request.execute()
  }

  return {
    restaurantId,
    restaurants,
    markers,
    activeRestaurant,
    loading,
    setRestaurantId,
    getRestaurants,
  }
}
